/*
 * Helpers del dashboard de Operaciones sin Facturar.
 * Replican la logica de calculo del HTML original (cleanRow, groupSum, buckets).
 */

//Metadata quemada de la carga original
export const carteraMeta = {
  sourceFile: "7_1_informe_Jefe_17-07-2026.xlsx",
  generatedAt: "24/07/2026 15:46",
  recordCount: 6549,
};

//Paleta de tokens del codekit usada por las gráficas del dashboard
export const carteraPalette = [
  "secondary-600", "primary-600", "secondary-800", "accent-600",
  "info-600", "success-600", "secondary-400", "warning-600",
];

//Etiquetas legibles de las claves de agrupacion
export const keyLabels = {
  GERENCIA: "Gerencia",
  NOMBRE: "Cliente",
  "TIPO DE OPERACIÓN": "Tipo de Operación",
  "DIRECTOR DE CUENTA": "Director de Cuenta",
};

/**
 * Formatea un numero como pesos colombianos
 * @param {number} nValue Valor a formatear
 * @returns {string} Valor en formato COP
 */
export const fmtCOP = (nValue) => "$" + Math.round(nValue || 0).toLocaleString("es-CO");

/**
 * Formatea un valor COP en millones abreviados para las barras horizontales
 * @param {number} nValue Valor a formatear
 * @returns {string} Valor abreviado (ej: $3.546 M)
 */
export const fmtMillions = (nValue) =>
  "$" + Math.round((nValue || 0) / 1e6).toLocaleString("es-CO") + " M";

/**
 * Convierte un valor COP a millones enteros (escala de las barras horizontales)
 * @param {number} nValue Valor a convertir
 * @returns {number} Valor en millones
 */
export const toMillions = (nValue) => Math.round((nValue || 0) / 1e6);

/**
 * Indica si un valor es numerico (para detectar columnas NOMBRE/ID intercambiadas)
 * @param {*} vValue Valor a evaluar
 * @returns {boolean}
 */
const isNumeric = (vValue) =>
  vValue !== null && vValue !== undefined && vValue !== "" && !isNaN(Number(vValue));

/**
 * Calcula la diferencia en dias completos entre hoy y una fecha
 * @param {Date} dDate Fecha base
 * @param {Date} dNow Fecha actual
 * @returns {number|null} Dias transcurridos o null si la fecha es invalida
 */
const diffDays = (dDate, dNow) => {
  if (!(dDate instanceof Date) || isNaN(dDate)) return null;
  return Math.floor((dNow - dDate) / (1000 * 60 * 60 * 24));
};

/**
 * Normaliza una fila cruda del dataset al formato que consume el dashboard
 * @param {Object} mRow Fila cruda del dataset
 * @param {Date} dNow Fecha actual para calcular antigüedad
 * @returns {Object} Fila normalizada con campos derivados
 */
const cleanRow = (mRow, dNow) => {
  const sGerencia = (mRow["GERENCIA"] || "").toString().trim().toUpperCase();
  let sTipo = mRow["TIPO DE OPERACIÓN"];
  if (sTipo === 0 || sTipo === "#N/A" || sTipo === null || sTipo === undefined || sTipo === "") {
    sTipo = "SIN CLASIFICAR";
  }
  let sDirector = mRow["DIRECTOR DE CUENTA"];
  if (!sDirector || sDirector === "NO REGISTRA" || sDirector === "NULL") sDirector = "Sin asignar";

  let vNombre = mRow["NOMBRE"];
  let vId = mRow["ID"];
  if (isNumeric(vNombre) && !isNumeric(vId) && vId) {
    const vTmp = vNombre;
    vNombre = vId;
    vId = vTmp;
  }

  const dCreacion = mRow["FECHA DE CREACIÓN"] ? new Date(mRow["FECHA DE CREACIÓN"]) : null;
  const dLevanteRaw = mRow["FECHA DE LEVANTE"] ? new Date(mRow["FECHA DE LEVANTE"]) : null;
  const bTieneLevante = dLevanteRaw instanceof Date && !isNaN(dLevanteRaw);
  const fmtFecha = (dDate) =>
    dDate instanceof Date && !isNaN(dDate) ? dDate.toLocaleDateString("es-CO") : null;

  return {
    NOMBRE: vNombre || "Sin nombre",
    ID: vId,
    GERENCIA: sGerencia || "SIN GERENCIA",
    DO: mRow["DO"],
    ANIO: mRow["AÑO DEL DO"],
    "TIPO DE OPERACIÓN": sTipo,
    "DIRECTOR DE CUENTA": sDirector,
    VALOR: Number(mRow["VALOR"]) || 0,
    _dias: diffDays(dCreacion, dNow),
    _tieneLevante: bTieneLevante,
    _diasLevante: bTieneLevante ? diffDays(dLevanteRaw, dNow) : null,
    _fechaCreacionStr: fmtFecha(dCreacion) || "Sin fecha",
    _fechaLevanteStr: bTieneLevante ? fmtFecha(dLevanteRaw) : "Sin levante",
  };
};

/**
 * Normaliza todo el dataset una sola vez
 * @param {Array} mDataset Dataset crudo
 * @returns {Array} Filas normalizadas con DO válido
 */
export const prepareRows = (mDataset) => {
  const dNow = new Date();
  return mDataset.map((mRow) => cleanRow(mRow, dNow)).filter((mRow) => mRow.DO);
};

/**
 * Suma el VALOR agrupando por una clave
 * @param {Array} mData Filas a agrupar
 * @param {string} sKey Clave de agrupacion
 * @returns {Object} Mapa clave → suma
 */
export const groupSum = (mData, sKey) => {
  const mMap = {};
  mData.forEach((mRow) => {
    const sK = mRow[sKey] ?? "N/A";
    mMap[sK] = (mMap[sK] || 0) + mRow.VALOR;
  });
  return mMap;
};

//Clasificaciones de semáforo por rango de días
export const bucketAgingGeneral = (nDias) => (nDias <= 30 ? "verde" : nDias <= 60 ? "amarillo" : "rojo");
export const bucketSinLevante = (nDias) => (nDias <= 7 ? "verde" : nDias <= 20 ? "amarillo" : "rojo");
export const bucketConLevante = (nDias) => (nDias <= 3 ? "verde" : nDias <= 7 ? "amarillo" : "rojo");

//Token de color del codekit por rango de semáforo
export const bucketTokens = { verde: "success-500", amarillo: "warning-500", rojo: "error-500" };

//Cuartetos de color (celda, tarjeta, ícono, texto) para las gráficas de dona PieCharts
export const pieQuads = [
  { colorCellChart: "secondary-600", bgColorCards: "secondary-100", iconColor: "secondary-600", textColor: "secondary-700" },
  { colorCellChart: "primary-600", bgColorCards: "primary-100", iconColor: "primary-600", textColor: "primary-700" },
  { colorCellChart: "accent-600", bgColorCards: "accent-100", iconColor: "accent-600", textColor: "accent-700" },
  { colorCellChart: "success-600", bgColorCards: "success-100", iconColor: "success-600", textColor: "success-700" },
  { colorCellChart: "warning-600", bgColorCards: "warning-100", iconColor: "warning-600", textColor: "warning-700" },
  { colorCellChart: "info-600", bgColorCards: "info-100", iconColor: "info-600", textColor: "info-700" },
];

//Etiquetas de cada rango de semáforo
export const bucketLabels = { verde: "En rango", amarillo: "Atención", rojo: "Crítico" };
